import { IconCheck } from '@tabler/icons-react';

interface ICheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  id?: string;
  disabled?: boolean;
  label?: string;
  className?: string;
  value?: string;
}

interface ICheckboxOption {
  value: string;
  label: string;
}

interface ICheckboxGroupProps {
  name: string;
  label?: string;
  options: ICheckboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  direction?: 'row' | 'column';
  gap?: string;
  disabled?: boolean;
}

export const Checkbox = ({
  checked,
  onChange,
  id = 'checkbox',
  disabled = false,
  label,
  className = '',
  value,
}: ICheckboxProps) => {
  return (
    <label
      htmlFor={id}
      className={`relative inline-flex items-center ${
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
      } ${className}`}
    >
      <input
        type="checkbox"
        id={id}
        checked={checked}
        value={value}
        onChange={() => onChange(!checked)}
        disabled={disabled}
        className="sr-only peer"
      />
      <div
        className={`
          w-5 h-5 rounded border-[1.5px] flex items-center justify-center transition-colors
          ${
            checked
              ? 'bg-brand-default border-brand-default'
              : 'bg-white border-neutral-light-200 hover:border-neutral-400'
          }
          ${disabled ? 'opacity-60' : ''}
        `}
      >
        {checked && <IconCheck size={16} className="text-white" />}
      </div>
      {label && (
        <span className="ms-2 text-sm font-medium text-neutral-primary">
          {label}
        </span>
      )}
    </label>
  );
};

export const CheckboxGroup = ({
  name,
  label,
  options,
  value,
  onChange,
  direction = 'row',
  gap = 'gap-4',
  disabled = false,
}: ICheckboxGroupProps) => {
  const handleToggle = (optionValue: string) => {
    const isSelected = value.includes(optionValue);
    if (isSelected) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {label && (
        <label className="block text-sm font-medium text-neutral-primary leading-5 capitalize">
          {label}
        </label>
      )}
      <div
        className={`flex ${direction === 'row' ? 'flex-row' : 'flex-col'} ${gap} items-center`}
      >
        {options.map((option) => (
          <Checkbox
            key={option.value}
            id={`${name}-${option.value}`}
            checked={value.includes(option.value)}
            onChange={() => handleToggle(option.value)}
            label={option.label}
            disabled={disabled}
            value={option.value}
          />
        ))}
      </div>
    </div>
  );
};
